import { Spinner } from '@optools/components/loading/spinner';
import { Progress } from '@optools/components/ui/progress';
import { usePolling } from '@optools/hooks/usePolling';
import { BTC_GAME_CONTENT_WITH_ASSETS } from '@optools/services/operator-gateway/btc/queries';
import { AssetState } from '@optools/services/operator-gateway/types/generated/graphql';
import { generateAssetKey } from '@optools/tools/btc-tools/utils/assets';
import { useParams, useSearch } from '@tanstack/react-router';
import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';

export const POLL_INTERVAL = 3000;
export const MAX_POLLING_ATTEMPTS = 10;
export const MAX_POLLING_ATTEMPTS_WARNING = MAX_POLLING_ATTEMPTS / 2;

export interface BulkAssetUpdatesProcessingProps {
  assetIds: Set<string>;
  assetState: AssetState | null;
  onComplete: () => void;
  onTimeout: () => void;
}

export function BulkAssetUpdatesProcessing({
  assetIds,
  assetState,
  onComplete,
  onTimeout,
}: BulkAssetUpdatesProcessingProps) {
  const { date } = useSearch({ from: '/_auth/btc-tools/schedule/$gamePk/pipelines/$feedType' });
  const { gamePk } = useParams({ from: '/_auth/btc-tools/schedule/$gamePk/pipelines/$feedType' });

  const [assetsProcessed, setAssetsProcessed] = useState<Set<string>>(new Set());

  const processed = assetsProcessed.size;
  const total = assetIds.size;
  const completed = processed === total && total > 0;

  // Poll for updates to the affected asset state
  const { attempts, startPolling } = usePolling({
    interval: POLL_INTERVAL,
    maxAttempts: MAX_POLLING_ATTEMPTS,
    onData: ({ game }) => {
      if (!game?.content) return;
      setAssetsProcessed((current) => {
        const newSet = new Set(current);
        game.content
          .flatMap((content) => content.assets)
          .forEach((asset) => {
            const assetKey = generateAssetKey(asset);
            if (asset.assetId && assetIds.has(assetKey)) {
              if (asset.state === assetState) {
                newSet.add(assetKey);
              }
            }
          });

        return newSet;
      });
    },
    onMaxAttemptsReached: () => {
      if (!completed) onTimeout();
    },
    query: BTC_GAME_CONTENT_WITH_ASSETS,
    shouldStopPolling: () => completed,
    variables: { gamePk, scheduleDate: date },
  });

  useEffect(() => {
    startPolling();
  }, [startPolling]);

  useEffect(() => {
    if (completed) {
      setTimeout(() => {
        onComplete();
      }, 1000);
    }
  }, [onComplete, completed]);

  return (
    <div>
      <div className="mb-2 h-5">
        {attempts > MAX_POLLING_ATTEMPTS_WARNING && (
          <p className="animate-fade-in text-sm text-muted-foreground">
            The updates are taking longer than expected...
          </p>
        )}
      </div>
      <Progress className="mb-4" value={(processed / total) * 100} />
      <p className="flex items-center gap-1 text-sm text-muted-foreground">
        {completed ? (
          <Check className="size-4 animate-fade-in" />
        ) : (
          <Spinner className="duration-500" variant="loader" />
        )}
        <span data-testid="processing-count">
          <span>
            {processed}/{total}
          </span>{' '}
          updates completed
        </span>
      </p>
    </div>
  );
}
